import { useMutation, type UseMutationOptions } from "@tanstack/react-query";

import { authClient, betterAuthErrorText } from "lib/auth/authClient";
import { SKIP_SENTRY_FOR_CLIENT_ERRORS } from "lib/sentryErrors";

type ConsentDecision = { accept: boolean; oauthQuery: string };

/**
 * Records the user's answer on the OIDC consent screen and returns where to send them next — back to
 * the client with a code on accept, or with `access_denied` on refuse.
 *
 * `oauthQuery` is the signed query the auth-service redirected here with, passed back **verbatim**:
 * it's how the provider resumes the exact request it paused, and the endpoint 400s without it.
 */
export function useOAuthConsent(
  options?: UseMutationOptions<string, Error, ConsentDecision, unknown>,
) {
  return useMutation({
    meta: SKIP_SENTRY_FOR_CLIENT_ERRORS,
    mutationKey: ["oauthConsent"],
    ...options,
    mutationFn: async ({ accept, oauthQuery }: ConsentDecision): Promise<string> => {
      const { data, error } = await authClient.$fetch<{ url?: string }>("/oauth2/consent", {
        method: "POST",
        body: { accept, oauth_query: oauthQuery },
      });
      if (error) {
        throw new Error(betterAuthErrorText(error, "We couldn't save your consent decision."), {
          cause: error,
        });
      }
      if (!data?.url) {
        throw new Error("We couldn't save your consent decision.");
      }
      return data.url;
    },
  });
}
